import React, { useCallback, useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import cx from "classnames";
import { message } from "antd";
import { useRouter } from "next/router";
import styles from "./index.module.scss";
import logoImg from "@/assets/images/logo.png";
import storeDark from "assets/images/common/store_dark.svg";
import storeLight from "assets/images/common/store.svg";
import playDark from "assets/images/common/Union_dark.svg";
import playLight from "assets/images/common/Union.svg";
import store from "store";
import { get_socialMediaList, type SocialMediaConfigItem } from "@/api/xt-app/social-media";
import { waitRes } from "utils/axios";
import { getHosts, getZendeskHcLocale } from "utils/method";
import useIsDesktop from "@/hooks/useIsDesktop";
import { IconSecChevronRight } from "@/components/icons/accountIcons";

const { useTranslation } = Hooks;

const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "0.0.0";

/** 清理所有 PWA 缓存并更新：删除 Cache API 缓存、注销当前 SW、刷新页面以拉取新版本 */
async function clearPwaCacheAndUpdate(): Promise<void> {
  if (typeof window === "undefined" || !window.caches) return;

  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch (e) {
    console.warn("[PWA] 清理缓存失败:", e);
  }

  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.unregister();
    } catch (e) {
      console.warn("[PWA] 注销 Service Worker 失败:", e);
    }
  }

  window.location.reload();
}

const CHEVRON_RIGHT = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ICON_ANDROID = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 65 65" fill="none" aria-hidden>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M48.4043 47.1484C48.4042 48.9309 46.9624 50.3896 45.2002 50.3896H42.5576V57.8105C42.5574 60.0408 41.1473 61.8661 39.4248 61.8661H38.7295C37.007 61.8661 35.5969 60.0408 35.5967 57.8105V50.3896H29.709V57.7245C29.7089 59.9552 28.3003 61.7809 26.5771 61.7812H25.8809C24.1583 61.7812 22.7481 59.9553 22.748 57.7245V50.3896H19.8672C18.1049 50.3896 16.6632 48.9309 16.6631 47.1484V21.4696H48.4043V47.1484ZM54.4033 21.747C56.1267 21.747 57.5361 23.5717 57.5361 25.8027V38.8163C57.5361 41.048 56.1266 42.8729 54.4033 42.873H53.707C51.9844 42.873 50.5743 41.048 50.5742 38.8163V25.8027C50.5742 23.5716 51.9844 21.747 53.707 21.747H54.4033ZM11.3291 21.6698C13.0523 21.6701 14.4609 23.4948 14.4609 25.7255V38.7392C14.4609 40.9695 13.0523 42.7955 11.3291 42.7958H10.6328C8.9098 42.7958 7.5 40.9697 7.5 38.7392V25.7255C7.5 23.4946 8.9098 21.6698 10.6328 21.6698H11.3291ZM42.2715 3.18156C42.4017 2.99142 42.661 2.9422 42.8496 3.07413L42.9248 3.12687C43.1134 3.25823 43.1615 3.522 43.0312 3.71183L39.9893 8.17374C44.3787 10.4075 47.5164 14.5214 48.166 19.3583H16.6543C17.3193 14.4106 20.5867 10.22 25.1338 8.02238L22.2422 3.78312C22.1127 3.59267 22.1601 3.32855 22.3486 3.19718L22.4248 3.14445C22.6133 3.01342 22.8735 3.0618 23.0029 3.25187L25.9941 7.63761C27.9558 6.82502 30.127 6.37395 32.4102 6.37394C34.8169 6.37394 37.0973 6.87483 39.1416 7.7714L42.2715 3.18156ZM25.7754 11.3876C24.9089 11.3878 24.2051 12.0994 24.2051 12.9765C24.2052 13.8533 24.909 14.5652 25.7754 14.5653C26.6429 14.5653 27.3465 13.8534 27.3467 12.9765C27.3467 12.0993 26.643 11.3876 25.7754 11.3876ZM39.3311 11.3876C38.4641 11.3876 37.7598 12.0993 37.7598 12.9765C37.7599 13.8534 38.4642 14.5653 39.3311 14.5653C40.1985 14.5652 40.9012 13.8534 40.9014 12.9765C40.9014 12.0994 40.1986 11.3877 39.3311 11.3876Z"
      fill="currentColor"
    />
  </svg>
);

const ICON_UPDATE = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <g clipPath="url(#clip0_about_update)">
      <path
        d="M9.97003 0.449951C4.68002 0.449951 0.400024 4.72995 0.400024 10.02C0.400024 15.31 4.68002 19.59 9.97003 19.59C15.26 19.59 19.54 15.31 19.54 10.02C19.54 4.72995 15.25 0.449951 9.97003 0.449951ZM11.69 6.05995L12.05 5.27995C12.39 4.73995 12.9 5.18995 12.9 5.18995L14.54 7.92995C14.84 8.48995 14.16 8.66995 14.16 8.66995L11.15 9.33995C10.08 9.49995 10.41 8.72995 10.41 8.72995L10.78 7.94995C6.29002 6.29995 4.82002 9.46995 4.82002 9.46995C6.14003 3.94995 11.69 6.05995 11.69 6.05995ZM8.12002 14.02L7.78003 14.83C7.45003 15.4 6.93003 14.94 6.93003 14.94L5.21003 12.16C4.89003 11.59 5.57002 11.39 5.57002 11.39L8.57002 10.63C9.64003 10.44 9.33003 11.24 9.33003 11.24L8.98003 12.05C13.53 13.64 14.92 10.35 14.92 10.35C13.74 16.05 8.12002 14.02 8.12002 14.02Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_about_update">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/** 是否以 PWA（独立窗口/桌面安装）方式运行 */
function useIsPwa() {
  const [isPwa, setIsPwa] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    const iosStandalone = !!(navigator as { standalone?: boolean }).standalone;
    setIsPwa(!!(standalone || iosStandalone));
  }, []);
  return isPwa;
}

function resolveSocialIconUrl(icon?: string): string | undefined {
  if (!icon || !icon.trim()) return undefined;
  const t = icon.trim();
  if (/^https?:\/\//i.test(t)) return t;
  const { host, protocol } = getHosts();
  const path = t.startsWith("/") ? t : `/${t}`;
  return `${protocol}//${host}${path}`;
}

type SocialLinkRow = {
  key: string;
  href: string;
  label: string;
  iconUrl?: string;
};

type AppDownloadRow = {
  key: string;
  href: string;
  label: string;
  icon: React.ReactNode;
};

type LegalLinkRow = {
  key: string;
  href: string;
  label: string;
};

const APP_DOWNLOAD_LINKS = [
  {
    key: "apple",
    href: "https://apps.apple.com/app/id6758935015",
    labelKey: "google.store",
  },
  {
    key: "google-play",
    href: "https://play.google.com/store/apps/details?id=com.spikex.app.droid",
    labelKey: "google.play",
  },
  {
    key: "android",
    href: "https://a.spikex.com/app/Spikex.apk",
    labelKey: "account.downloadAndroidApk",
  },
] as const;

/** CMS/API 常返回英文展示名，按关键词映射到 i18n */
function resolveSocialLabel(name: string, t: (key: string) => string): string {
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();
  if (lower.includes("twitter")) return t("account.followUsOnTwitter");
  if (lower.includes("telegram")) return t("account.joinUsOnTelegram");
  if (lower.includes("linkedin")) return t("account.followUsOnLinkedin");
  return trimmed;
}

const UserAbout: React.FC = () => {
  const t = useTranslation();
  const { locale } = useRouter();
  const { isDesktop, isReady } = useIsDesktop();
  const isSpikexPc = isReady && isDesktop;
  const [updating, setUpdating] = useState(false);
  const [socialFromApi, setSocialFromApi] = useState<SocialMediaConfigItem[] | null>(null);
  const isPwa = useIsPwa();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, err } = await waitRes(get_socialMediaList, {});
      if (cancelled) return;
      if (err || !Array.isArray(data)) {
        setSocialFromApi([]);
        return;
      }
      setSocialFromApi(data as SocialMediaConfigItem[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClearCache = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (updating) return;
      if (typeof window === "undefined" || !("caches" in window)) {
        message.info(t("account.clearCacheUnsupported") || "Clear cache is not supported in this environment");
        return;
      }
      setUpdating(true);
      message.loading(t("account.clearCacheIng") || "Clearing cache…", 0);
      try {
        await clearPwaCacheAndUpdate();
      } catch (err) {
        message.destroy();
        message.error(t("account.clearCacheFailed") || "Clear cache failed, please try again later");
        setUpdating(false);
      }
    },
    [t, updating]
  );

  const socialLinkRows = useMemo((): SocialLinkRow[] => {
    if (!socialFromApi?.length) return [];
    return socialFromApi.map((item) => ({
      key: `social-${item.id}`,
      href: item.linkUrl,
      label: resolveSocialLabel(item.name, t),
      iconUrl: resolveSocialIconUrl(item.icon),
    }));
  }, [socialFromApi, t]);

  const socialListReady = socialFromApi !== null;
  const showSocialEmpty = socialListReady && socialLinkRows.length === 0;

  const appDownloadRows = useMemo((): AppDownloadRow[] => {
    const isDark = store.app.isDark;
    const appleIcon = isDark ? storeDark : storeLight;
    const playIcon = isDark ? playDark : playLight;
    return APP_DOWNLOAD_LINKS.map((item) => ({
      key: item.key,
      href: item.href,
      label: t(item.labelKey),
      icon:
        item.key === "android" ? (
          ICON_ANDROID
        ) : (
          <img src={item.key === "apple" ? appleIcon : playIcon} alt="" />
        ),
    }));
  }, [t, store.app.isDark]);

  const legalLinkRows = useMemo((): LegalLinkRow[] => {
    const hc = getZendeskHcLocale(locale);
    return [
      {
        key: "terms",
        href: `https://spikex.zendesk.com/hc/${hc}/articles/5158356356894`,
        label: t("account.termsOfService") || "Terms of Service",
      },
      {
        key: "privacy",
        href: `https://spikex.zendesk.com/hc/${hc}/articles/5158509865246`,
        label: t("register.policy") || "Privacy Policy",
      },
      {
        key: "help",
        href: `https://spikex.zendesk.com/hc/${hc}`,
        label: t("account.helpCenter") || "Help Center",
      },
    ];
  }, [locale, t]);

  const chevron = isSpikexPc ? <IconSecChevronRight className={styles["follow-chevron-icon"]} /> : CHEVRON_RIGHT;

  return (
    <div className={cx(styles["about-wrapper"], isSpikexPc && styles.spikexPc)}>
      {!isSpikexPc ? (
        <header className={styles.mobileHeader}>
          <h1 className={styles.mobileTitle}>{t("account.about")}</h1>
          <p className={styles.mobileSubtitle}>{t("account.aboutSubtitle")}</p>
        </header>
      ) : null}

      {!isSpikexPc ? (
        <div className={cx(styles["about-card"], styles["base-card"])}>
          <div className={styles["about-icon"]}>
            <div className={styles["about-icon-inner"]}>
              <img src={logoImg} alt="Spikex" className={styles["about-logo"]} />
            </div>
          </div>
          <div className={styles["about-version"]}>V {APP_VERSION}</div>
        </div>
      ) : null}

      <div className={cx(styles["legal-card"], styles["base-card"])}>
        <div className={styles["legal-list"]}>
          {legalLinkRows.map((item) => (
            <a
              key={item.key}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles["legal-item"]}
            >
              <span className={styles["legal-label"]}>{item.label}</span>
              <span className={styles["follow-chevron"]}>{chevron}</span>
            </a>
          ))}
        </div>
      </div>

      <div className={cx(styles["follow-card"], styles["base-card"])}>
        <div className={styles["follow-title"]}>{t("account.followUs")}</div>
        <div className={styles["follow-list"]}>
          {showSocialEmpty ? (
            <div className={styles["follow-empty"]}>{t("account.noData")}</div>
          ) : (
            socialLinkRows.map((item) => {
              const iconEl =
                item.iconUrl != null ? (
                  <img src={item.iconUrl} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className={styles["follow-icon-fallback"]}>{item.label.trim().charAt(0).toUpperCase()}</span>
                );
              const isHttp = item.href.startsWith("http");
              return (
                <a
                  key={item.key}
                  href={item.href}
                  target={isHttp ? "_blank" : undefined}
                  rel={isHttp ? "noopener noreferrer" : undefined}
                  className={styles["follow-item"]}
                >
                  <span className={styles["follow-icon"]}>{iconEl}</span>
                  <span className={styles["follow-label"]}>{item.label}</span>
                  <span className={styles["follow-chevron"]}>{chevron}</span>
                </a>
              );
            })
          )}
          {isPwa && socialListReady && (
            <a
              href="#"
              className={cx(styles["follow-item"], updating && styles["follow-item-disabled"])}
              onClick={handleClearCache}
              aria-disabled={updating ? true : undefined}
            >
              <span className={styles["follow-icon"]}>{ICON_UPDATE}</span>
              <span className={styles["follow-label"]}>{t("account.clearCache")}</span>
              <span className={styles["follow-chevron"]}>{chevron}</span>
            </a>
          )}
        </div>
      </div>

      <div className={cx(styles["follow-card"], styles["base-card"])}>
        <div className={styles["follow-title"]}>{t("account.downloadApp")}</div>
        <div className={styles["follow-list"]}>
          {appDownloadRows.map((item) => (
            <a
              key={item.key}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles["follow-item"]}
            >
              <span className={styles["follow-icon"]}>{item.icon}</span>
              <span className={styles["follow-label"]}>{item.label}</span>
              <span className={styles["follow-chevron"]}>{chevron}</span>
            </a>
          ))}
        </div>
      </div>

      {isSpikexPc ? (
        <p className={styles.copyright}>
          {t("account.aboutCopyright") || `© ${new Date().getFullYear()} SpikeX. ${t("footer.Rights") || "All rights reserved."}`}
        </p>
      ) : null}
    </div>
  );
};

export default observer(UserAbout);
